import { ReactNode } from "react";
import { InputV2 } from "../inputs/Input";
import { ValidationProps } from "./types";
import { createToast } from "#src/common/system/toasts";
import { XClose } from "@untitled-ui/icons-react";
import { FormFieldWrapper } from "./helpers/FieldWrapper";
import { useTranslation } from "react-i18next";

interface IFormInputMultipleV2Props {
  label: string;
  values: string[];
  onChangeValues: (newValue: string[]) => void;
  input: string;
  onChangeInput: (newInput: string) => void;
  required?: boolean;
  placeholder?: string;
  hint?: ReactNode;
  validation?: {
    pattern?: ValidationProps<RegExp>;
  };
  column?: number;
}

export const FormInputMultipleV2 = ({
  label,
  values,
  onChangeValues,
  input,
  onChangeInput,
  required = false,
  placeholder,
  hint,
  validation,
  column,
}: IFormInputMultipleV2Props) => {
  const { t } = useTranslation("common", { keyPrefix: "v2.form" });

  const onCreateNewInput = (pushErrorToast: boolean) => {
    if (input.length === 0) {
      if (pushErrorToast) {
        createToast({
          type: "warning",
          message: t("validation.required"),
        });
      }
      return;
    }

    if (validation && validation.pattern) {
      if (validation.pattern instanceof RegExp) {
        if (!validation.pattern.test(input)) {
          if (pushErrorToast) {
            createToast({
              type: "warning",
              message: t("validation.pattern"),
            });
          }
          return;
        }
      } else {
        if (!validation.pattern.value.test(input)) {
          if (validation.pattern.errorMessage) {
            if (pushErrorToast) {
              createToast({
                type: "warning",
                message: validation.pattern.errorMessage(),
              });
            }
          }
          return;
        }
      }
    }
    onChangeValues([...values, input]);
    onChangeInput("");
  };

  const onRemoveValue = (itemIndex: number) => {
    onChangeValues(values.filter((_, index) => index !== itemIndex));
  };

  return (
    <FormFieldWrapper
      label={label}
      requiredMark={required}
      error={null}
      hint={hint}
    >
      <div
        style={{ gridTemplateColumns: `repeat(${column}, minmax(0, 1fr))` }}
        className="grid gap-x-6 gap-y-1.5"
      >
        {values.map((item, index) => (
          <div
            key={index}
            className="bg-white border border-gray-v2-300 shadow-xs rounded-lg px-3 py-2 text-md text-gray-v2-900 flex items-center justify-between"
          >
            {item}
            <XClose
              className="h-4 w-4 min-w-4 text-gray-v2-400 cursor-pointer"
              onClick={() => {
                onRemoveValue(index);
              }}
            />
          </div>
        ))}
        <InputV2
          className="w-full"
          placeholder={placeholder}
          value={input}
          onChange={(e) => {
            onChangeInput(e.target.value);
          }}
          onBlur={() => {
            onCreateNewInput(false);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              onCreateNewInput(true);
            }
          }}
        />
      </div>
    </FormFieldWrapper>
  );
};
